import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Eye, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
interface SupplierListProps {
  searchQuery: string;
  filterStatus: string;
}
export function SupplierList({
  searchQuery,
  filterStatus
}: SupplierListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const suppliers = [{
    id: 1,
    name: 'Napa Valley Vineyards',
    type: 'Grape Supplier',
    status: 'compliant',
    certificationsRequired: 8,
    certificationsCompleted: 8,
    expiringCertifications: 0,
    lastUpdated: '2 days ago',
    riskLevel: 'low'
  }, {
    id: 2,
    name: 'Sonoma Organic Growers',
    type: 'Grape Supplier',
    status: 'at-risk',
    certificationsRequired: 6,
    certificationsCompleted: 6,
    expiringCertifications: 2,
    lastUpdated: '5 days ago',
    riskLevel: 'medium'
  }, {
    id: 3,
    name: 'Artisan Cork Suppliers',
    type: 'Cork Provider',
    status: 'non-compliant',
    certificationsRequired: 10,
    certificationsCompleted: 7,
    expiringCertifications: 1,
    lastUpdated: '1 day ago',
    riskLevel: 'high'
  }, {
    id: 4,
    name: 'Premium Wine Bottles Inc.',
    type: 'Bottle Supplier',
    status: 'partial',
    certificationsRequired: 5,
    certificationsCompleted: 4,
    expiringCertifications: 0,
    lastUpdated: '1 week ago',
    riskLevel: 'medium'
  }, {
    id: 5,
    name: 'Eco-Friendly Wine Packaging',
    type: 'Packaging',
    status: 'compliant',
    certificationsRequired: 7,
    certificationsCompleted: 7,
    expiringCertifications: 1,
    lastUpdated: '3 days ago',
    riskLevel: 'low'
  }, {
    id: 6,
    name: 'Willamette Valley Growers',
    type: 'Grape Supplier',
    status: 'non-compliant',
    certificationsRequired: 9,
    certificationsCompleted: 5,
    expiringCertifications: 2,
    lastUpdated: '1 day ago',
    riskLevel: 'high'
  }, {
    id: 7,
    name: 'Vintage Oak Barrels',
    type: 'Barrel Supplier',
    status: 'partial',
    certificationsRequired: 8,
    certificationsCompleted: 6,
    expiringCertifications: 0,
    lastUpdated: '4 days ago',
    riskLevel: 'medium'
  }, {
    id: 8,
    name: 'Wine Country Logistics',
    type: 'Distribution',
    status: 'at-risk',
    certificationsRequired: 5,
    certificationsCompleted: 5,
    expiringCertifications: 3,
    lastUpdated: '2 days ago',
    riskLevel: 'medium'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-4 h-4 text-green-600" data-id="element-4" />;
      case 'at-risk':
        return <Clock className="w-4 h-4 text-yellow-600" data-id="element-5" />;
      case 'non-compliant':
        return <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-6" />;
      case 'partial':
        return <Shield className="w-4 h-4 text-orange-600" data-id="element-7" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800';
      case 'at-risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'non-compliant':
        return 'bg-red-100 text-red-800';
      case 'partial':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'Compliant';
      case 'at-risk':
        return 'At Risk';
      case 'non-compliant':
        return 'Non-Compliant';
      case 'partial':
        return 'Partially Compliant';
      default:
        return status;
    }
  };
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = searchQuery === '' || supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) || supplier.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || supplier.status === filterStatus;
    return matchesSearch && matchesFilter;
  });
  const totalPages = Math.ceil(filteredSuppliers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, startIndex + rowsPerPage);
  return <div className="bg-white rounded-lg border" data-id="element-8">
      <div className="p-6 border-b border-gray-200" data-id="element-9">
        <div className="flex items-center justify-between" data-id="element-10">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-11">
            Wine Supplier Overview
          </h2>
          <span className="text-sm text-gray-600" data-id="element-12">
            {filteredSuppliers.length} suppliers
          </span>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-13">
        <table className="w-full" data-id="element-14">
          <thead className="bg-gray-50" data-id="element-15">
            <tr data-id="element-16">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17">
                Supplier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19">
                Certifications
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20">
                Last Updated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-21">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-22">
            {paginatedSuppliers.map(supplier => <tr key={supplier.id} className="hover:bg-gray-50" data-id="element-23">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-24">
                  <div data-id="element-25">
                    <div className="text-sm font-medium text-gray-900" data-id="element-26">
                      {supplier.name}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-27">{supplier.type}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-28">
                  <div className="flex items-center space-x-2" data-id="element-29">
                    {getStatusIcon(supplier.status)}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(supplier.status)}`} data-id="element-30">
                      {getStatusText(supplier.status)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-31">
                  <div className="text-sm text-gray-900" data-id="element-32">
                    {supplier.certificationsCompleted} of{' '}
                    {supplier.certificationsRequired} Complete
                  </div>
                  {supplier.expiringCertifications > 0 && <div className="text-sm text-yellow-600" data-id="element-33">
                      {supplier.expiringCertifications} Expiring Soon
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-34">
                  {supplier.lastUpdated}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-35">
                  <button className="text-purple-600 hover:text-purple-700 flex items-center space-x-1 ml-auto" data-id="element-36">
                    <Eye className="w-4 h-4" data-id="element-37" />
                    <span data-id="element-38">View Details</span>
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-39">
        <div className="flex items-center" data-id="element-40">
          <select value={rowsPerPage} onChange={e => {
          setRowsPerPage(Number(e.target.value));
          setCurrentPage(1);
        }} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-41">
            <option value={5} data-id="element-42">5 rows</option>
            <option value={10} data-id="element-43">10 rows</option>
            <option value={20} data-id="element-44">20 rows</option>
            <option value={50} data-id="element-45">50 rows</option>
          </select>
          <span className="ml-4 text-sm text-gray-600" data-id="element-46">
            Showing {startIndex + 1} to{' '}
            {Math.min(startIndex + rowsPerPage, filteredSuppliers.length)} of{' '}
            {filteredSuppliers.length} entries
          </span>
        </div>
        <div className="flex items-center space-x-2" data-id="element-47">
          <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-lg border enabled:hover:bg-gray-50 disabled:opacity-50" data-id="element-48">
            <ChevronLeft className="w-5 h-5" data-id="element-49" />
          </button>
          <div className="text-sm text-gray-600" data-id="element-50">
            Page {currentPage} of {totalPages}
          </div>
          <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-lg border enabled:hover:bg-gray-50 disabled:opacity-50" data-id="element-51">
            <ChevronRight className="w-5 h-5" data-id="element-52" />
          </button>
        </div>
      </div>
    </div>;
}